import {
  DropdownButton,
  DropdownButtonContext,
  Tooltip,
} from '@dixa/lib-shared-habla';
import { ConversationLanguageCode } from 'lib-shared-languages';
import { useCallback, useRef, useState } from 'react';
import { useFormContext, useWatch } from 'react-hook-form';

import {
  OnLanguageChange,
  TranslateTextLanguageSelectionList,
} from 'components/translateTextLanguageSelectionList';
import { useTranslateText } from 'utils/useTranslateText';

import type { FormValues } from '../composeEmailModal.types';

import styles from './subjectTranslateButton.module.scss';

const subjectTranslationFailText =
  "Couldn't translate the subject. Try again or edit it by hand.";

type Translation = {
  readonly languageCode: ConversationLanguageCode;
  readonly original: string;
  readonly translated: string;
};

type SubjectTranslateButtonProps = {
  readonly onChange: (subject: string) => void;
  readonly onError: (message: string) => void;
};

/** Translates an email subject in place, with a way back to the original. */
export const SubjectTranslateButton = ({
  onChange,
  onError,
}: SubjectTranslateButtonProps): JSX.Element => {
  const { control } = useFormContext<FormValues>();
  const subject = useWatch({ control, name: 'subject' }) ?? '';
  const [translate, { asyncStatus }] = useTranslateText();
  const [translation, setTranslation] = useState<Translation>();
  const isTranslating = asyncStatus === 'pending';
  const isDisabled = isTranslating || !subject.trim();
  const latestSubject = useRef(subject);
  latestSubject.current = subject;

  // A subject edited after translating has no original to go back to.
  const currentTranslation =
    translation?.translated === subject ? translation : undefined;

  const handleLanguageChange = useCallback<OnLanguageChange>(
    async ({ selectedLanguage }) => {
      if (!selectedLanguage) {
        return;
      }

      const original = currentTranslation?.original ?? subject;

      try {
        const { translatedText } = await translate({
          language: selectedLanguage.name,
          text: original,
          contentType: 'text',
          direction: 'outbound',
        });

        if (!translatedText) {
          throw new Error('Empty translation');
        }

        // Don't overwrite what the agent typed while waiting.
        if (latestSubject.current !== subject) {
          return;
        }

        setTranslation({
          languageCode: selectedLanguage.code,
          original,
          translated: translatedText,
        });
        onChange(translatedText);
      } catch {
        onError(subjectTranslationFailText);
      }
    },
    [currentTranslation?.original, onChange, onError, subject, translate],
  );

  const handleRevert = useCallback(() => {
    if (currentTranslation) {
      onChange(currentTranslation.original);
      setTranslation(undefined);
    }
  }, [currentTranslation, onChange]);

  return (
    <Tooltip content="Translate subject">
      <DropdownButton
        fixed
        noArrow
        icon="translate"
        className={styles.root}
        buttonProps={{
          variant: 'tertiary',
          className: styles.trigger,
          isLoading: isTranslating,
          disabled: isDisabled,
          'aria-label': 'Translate subject',
        }}
        dropdown={
          <DropdownButtonContext.Consumer>
            {({ dropdownOpen, closeDropdown }) => (
              <TranslateTextLanguageSelectionList
                autoFocusSearch={dropdownOpen}
                selectedLanguageCode={currentTranslation?.languageCode}
                revertLabel="Show original"
                onRevert={currentTranslation ? handleRevert : undefined}
                onLanguageChange={(event) => {
                  closeDropdown();
                  void handleLanguageChange(event);
                }}
              />
            )}
          </DropdownButtonContext.Consumer>
        }
      />
    </Tooltip>
  );
};
